import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/BackButton";
import { FormField, SelectField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import { ProfitabilityTabs } from "@/components/ProfitabilityTabs";
import { calcLandedCost, useApp } from "@/context/AppContext";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/breakeven")({
  component: () => (
    <ManagerOnly>
      <BreakEvenPage />
    </ManagerOnly>
  ),
});

const PLATFORM_OPTIONS = [
  "eBay",
  "TCGplayer",
  "Facebook Marketplace",
  "Local",
  "Custom",
];
// NOTE (TNK-48/46): see tools.roi.tsx — fees should read from a shared Settings
// "default fees" source, and this screen should merge with ROI into a single
// "Profit Tools" screen. docs/settings-audit.md + docs/tools-organization-review.md.
const PLATFORM_FEES: Record<string, number> = {
  eBay: 13.25,
  TCGplayer: 10.25,
  "Facebook Marketplace": 5,
  Local: 0,
  Custom: 0,
};

const MANUAL_OPTION = "__manual__";

interface Result {
  breakEvenUnits: number;
  breakEvenRevenue: number;
  pctSoldNeeded: number;
  profitAtFull: number;
  roiAtFull: number;
  projectedProfit: number;
  projectedROI: number;
  netPerSale: number;
  costPerUnit: number;
}

function BreakEvenPage() {
  const { lots } = useApp();

  const [selectedLotId, setSelectedLotId] = useState<string>(MANUAL_OPTION);
  const [totalCost, setTotalCost] = useState("");
  const [totalUnits, setTotalUnits] = useState("");
  const [avgSalePrice, setAvgSalePrice] = useState("");
  const [platform, setPlatform] = useState("eBay");
  const [customFee, setCustomFee] = useState("13.25");
  const [paymentFee, setPaymentFee] = useState("2.9");
  const [shippingPerSale, setShippingPerSale] = useState("");
  const [packagingPerSale, setPackagingPerSale] = useState("");
  const [unitsSold, setUnitsSold] = useState("");

  const [result, setResult] = useState<Result | null>(null);

  const lotOptions = useMemo(
    () => [MANUAL_OPTION, ...lots.map((l) => l.id)],
    [lots],
  );
  const lotOptionLabels = useMemo(
    () => [
      "Manual Entry",
      ...lots.map(
        (l) => `${l.seller || "Lot"} — $${calcLandedCost(l).toFixed(2)}`,
      ),
    ],
    [lots],
  );

  const handleLotSelect = (id: string) => {
    setSelectedLotId(id);
    if (id === MANUAL_OPTION) return;
    const lot = lots.find((l) => l.id === id);
    if (!lot) return;
    setTotalCost(calcLandedCost(lot).toFixed(2));
    const totalCards =
      lot.sortingBins.reduce((a, b) => a + (b.count || 0), 0) ||
      lot.estimatedCards ||
      0;
    if (totalCards > 0) setTotalUnits(String(totalCards));
  };

  const platformFeeRate =
    platform === "Custom"
      ? parseFloat(customFee) || 0
      : (PLATFORM_FEES[platform] ?? 0);

  const calculate = () => {
    const cost = parseFloat(totalCost) || 0;
    const units = parseFloat(totalUnits) || 0;
    const sell = parseFloat(avgSalePrice) || 0;
    const shipping = parseFloat(shippingPerSale) || 0;
    const packaging = parseFloat(packagingPerSale) || 0;
    const sold = parseFloat(unitsSold) || 0;

    const feeRate = (platformFeeRate + (parseFloat(paymentFee) || 2.9)) / 100;
    const netPerSale = sell * (1 - feeRate) - shipping - packaging;
    const costPerUnit = units > 0 ? cost / units : 0;

    if (netPerSale <= 0) {
      setResult(null);
      return;
    }

    const breakEvenUnits = Math.ceil(cost / netPerSale);
    const breakEvenRevenue = breakEvenUnits * sell;
    const pctSoldNeeded = units > 0 ? (breakEvenUnits / units) * 100 : 0;
    const profitAtFull = units * netPerSale - cost;
    const roiAtFull = cost > 0 ? (profitAtFull / cost) * 100 : 0;
    const projectedProfit = sold * netPerSale - cost;
    const projectedROI = cost > 0 ? (projectedProfit / cost) * 100 : 0;

    setResult({
      breakEvenUnits,
      breakEvenRevenue,
      pctSoldNeeded,
      profitAtFull,
      roiAtFull,
      projectedProfit,
      projectedROI,
      netPerSale,
      costPerUnit,
    });
  };

  const soldNum = parseFloat(unitsSold) || 0;
  const unitsNum = parseFloat(totalUnits) || 0;
  const pctSold = unitsNum > 0 ? (soldNum / unitsNum) * 100 : 0;

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Profitability</h1>
        <p className="text-sm text-muted-foreground">
          Break-even — find how many sales you need to recover your lot
          investment.
        </p>
      </div>

      <ProfitabilityTabs current="breakeven" />

      <div
        className="flex-1 space-y-4 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {/* Lot section */}
        <Section title="Lot">
          {lots.length > 0 && (
            <SelectField
              label="Auto-fill from Lot"
              value={selectedLotId}
              options={lotOptions}
              optionLabels={lotOptionLabels}
              onSelect={handleLotSelect}
            />
          )}
          <FormField
            label="Total Cost Invested ($)"
            value={totalCost}
            onChangeText={setTotalCost}
            inputMode="decimal"
            placeholder="e.g. 120.00"
            required
          />
          <FormField
            label="Total Units / Cards"
            value={totalUnits}
            onChangeText={setTotalUnits}
            inputMode="decimal"
            placeholder="e.g. 500"
          />
          <FormField
            label="Units Already Sold"
            value={unitsSold}
            onChangeText={setUnitsSold}
            inputMode="decimal"
            placeholder="0"
          />
          {unitsNum > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs text-muted-foreground">
                {pctSold.toFixed(0)}% sold ({soldNum} of {unitsNum} units)
              </div>
              <ProgressTrack pct={pctSold} fillClass="bg-secondary" />
            </div>
          )}
        </Section>

        {/* Sale Details */}
        <Section title="Sale Details">
          <FormField
            label="Avg Sale Price ($)"
            value={avgSalePrice}
            onChangeText={setAvgSalePrice}
            inputMode="decimal"
            placeholder="e.g. 4.99"
            required
          />
          <SelectField
            label="Platform"
            value={platform}
            options={PLATFORM_OPTIONS}
            onSelect={setPlatform}
          />
          {platform === "Custom" && (
            <FormField
              label="Platform Fee %"
              value={customFee}
              onChangeText={setCustomFee}
              inputMode="decimal"
              placeholder="13.25"
            />
          )}
          <FormField
            label="Payment Fee %"
            value={paymentFee}
            onChangeText={setPaymentFee}
            inputMode="decimal"
            placeholder="2.9"
          />
          <FormField
            label="Shipping per Sale ($)"
            value={shippingPerSale}
            onChangeText={setShippingPerSale}
            inputMode="decimal"
            placeholder="0.00"
          />
          <FormField
            label="Packaging per Sale ($)"
            value={packagingPerSale}
            onChangeText={setPackagingPerSale}
            inputMode="decimal"
            placeholder="0.00"
          />
        </Section>

        <PrimaryButton
          label="Calculate Break-Even"
          onClick={calculate}
          className="w-full"
        />

        {result && (
          <div className="space-y-3 rounded-md bg-primary p-4">
            <div className="text-[11px] font-medium uppercase tracking-wide text-primary-foreground/60">
              Break-Even Analysis
            </div>

            <div className="flex flex-col items-center gap-1 border-b border-white/10 pb-4">
              <div className="text-5xl font-bold leading-none text-secondary">
                {result.breakEvenUnits}
              </div>
              <div className="text-sm text-primary-foreground/70">
                sales needed to break even
              </div>
            </div>

            {unitsNum > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-primary-foreground/80">
                  {result.pctSoldNeeded.toFixed(0)}% of inventory to sell
                </div>
                <div className="relative h-2.5 overflow-hidden rounded-full bg-white/15">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-secondary"
                    style={{
                      width: `${Math.min(100, result.pctSoldNeeded)}%`,
                    }}
                  />
                  {soldNum > 0 && (
                    <div
                      className="absolute inset-y-0 w-0.5 bg-emerald-500"
                      style={{ left: `${Math.min(100, pctSold)}%` }}
                    />
                  )}
                </div>
                <div className="flex gap-4 text-[11px] font-medium">
                  <span className="text-secondary">■ Break-even</span>
                  {soldNum > 0 && (
                    <span className="text-emerald-400">■ Sold so far</span>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-0">
              {[
                [
                  "Break-Even Revenue",
                  `$${result.breakEvenRevenue.toFixed(2)}`,
                ],
                [
                  "Net Per Sale (after fees)",
                  `$${result.netPerSale.toFixed(2)}`,
                ],
                ["Cost Per Unit", `$${result.costPerUnit.toFixed(2)}`],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-center justify-between border-b border-white/10 py-2"
                >
                  <div className="text-sm text-primary-foreground/70">
                    {label}
                  </div>
                  <div className="text-sm font-semibold text-primary-foreground">
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <ScenarioBox
              title="If all units sell"
              profit={result.profitAtFull}
              roi={result.roiAtFull}
            />

            {soldNum > 0 && (
              <ScenarioBox
                title={`At current sold (${soldNum} units)`}
                profit={result.projectedProfit}
                roi={result.projectedROI}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1 rounded-md border border-border bg-card p-4">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function ProgressTrack({ pct, fillClass }: { pct: number; fillClass: string }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="h-2.5 overflow-hidden rounded-full bg-border">
      <div
        className={`h-full rounded-full ${fillClass}`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function ScenarioBox({
  title,
  profit,
  roi,
}: {
  title: string;
  profit: number;
  roi: number;
}) {
  const positive = profit >= 0;
  const profitColor = positive ? "text-secondary" : "text-rose-400";
  return (
    <div className="space-y-2.5 rounded-md bg-white/5 p-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-primary-foreground/60">
        {title}
      </div>
      <div className="flex gap-6">
        <div>
          <div className={`text-2xl font-bold ${profitColor}`}>
            {positive ? "+" : ""}${profit.toFixed(2)}
          </div>
          <div className="text-[11px] text-primary-foreground/70">
            Net Profit
          </div>
        </div>
        <div>
          <div className={`text-2xl font-bold ${profitColor}`}>
            {roi.toFixed(0)}%
          </div>
          <div className="text-[11px] text-primary-foreground/70">ROI</div>
        </div>
      </div>
    </div>
  );
}
